/*******************************************************************************************
   _____            __              ____   ____                         .__               
  /  _  \   _______/  |________  ___\   \ /   /____   ____  __ __  _____|__|____    ____  
 /  /_\  \ /  ___/\   __\_  __ \/  _ \   Y   // __ \ /    \|  |  \/  ___/  \__  \  /    \ 
/    |    \\___ \  |  |  |  | \(  <_> )     /\  ___/|   |  \  |  /\___ \|  |/ __ \|   |  \
\____|__  /____  > |__|  |__|   \____/ \___/  \___  >___|  /____//____  >__(____  /___|  /
        \/     \/                                 \/     \/           \/        \/     \/ 
********************************************************************************************
Undo Zoom on Rotate Hook
********************************************************************************************

Author:     Nicholas Hamilton, PhD
Email:      nicholasehamilton@gmail.com
Date:       4th May 2025

*******************************************************************************************/
import React                            from 'react';
import { useLocation }                  from 'react-router-dom';
import debounce                         from 'lodash/debounce';
import { Device }                       from '@capacitor/device';


/**
 * React hook that monitors orientation changes, viewport resizes,
 * and route changes to undo unwanted zoom scaling (especially on mobile).
 *
 * It achieves this by detecting the `visualViewport.scale` and applying
 * a CSS transform to reset the zoom level visually.
 *
 * @param {Object} [options={}] - Optional configuration.
 * @param {boolean} [options.disabled=false] - Whether the effect is disabled.
 * @param {number} [options.debounceInterval=100] - Debounce interval in milliseconds for resize/orientation events.
 *
 * @returns {null} - This hook does not render anything.
 *
 * @example
 * useUndoZoomOnRotate({
 *   disabled: false,
 *   debounceInterval: 200,
 * });
 */
export const useUndoZoomOnRotate = ({
    disabled            = false, 
    debounceInterval    = 100
} = {}) => {

    const location = useLocation();

    // Correct Zoom
    const fixZoom = React.useCallback(() => {

        if(disabled) 
            return;

        const scale = window.visualViewport?.scale;
        if (scale && scale !== 1) {
            document.body.style.transform       = `scale(${1 / scale})`;
            document.body.style.transformOrigin = 'top left';
        } else {
            document.body.style.transform       = '';
        }
    },[disabled]);

    // Debounced handler
    const debouncedFixZoom = React.useMemo(
        () => debounce(fixZoom, debounceInterval),
        [fixZoom, debounceInterval]
    );

    // Listen to resize and orientation changes
    React.useEffect(() => {
        
        // Capacitor device-specific listener for orientation change
        Device.addListener('orientationchange', debouncedFixZoom);
        
        // Listen to resize events on the visual viewport
        window.visualViewport?.addEventListener('resize', debouncedFixZoom);

        return () => {
            Device.removeListener('orientationchange', debouncedFixZoom);
            window.visualViewport?.removeEventListener('resize', debouncedFixZoom);
            debouncedFixZoom.cancel();
        };

    }, [debouncedFixZoom]);

    // Handle zoom reset on route change
    React.useEffect(() => {
        debouncedFixZoom();
    }, [location?.pathname, debouncedFixZoom]);

    return null;
};

export default useUndoZoomOnRotate;